import React, { useEffect, useState, useMemo } from 'react';
import {
    Users, Newspaper, ShieldAlert, MessageSquare,
    ArrowUpRight, ArrowDownRight, Activity,
    Globe, UserPlus, Loader2,
    ChevronRight, ArrowRight
} from 'lucide-react';
import { 
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, 
    CartesianGrid, Legend, PieChart, Pie, Cell 
} from 'recharts';
import { User } from '../../../types';
import { fetchAdminChartsData } from '../../../services/adminService';
import { useLanguage } from '../../../context/LanguageContext';
import { axt as at } from '../../../i18nAdminExtended';
import { t } from '../../../i18n';

interface OverviewTabProps {
    stats: any;
    allUsers: User[];
    onTabSwitch: (tab: any) => void;
}

const COLORS = ['#0f172a', '#f43f5e', '#6366f1', '#10b981', '#f59e0b', '#8b5cf6', '#f97316', '#06b6d4'];

const LEAGUE_NAME_MAP: Record<string, string> = {
    '39': 'Premier League',
    '140': 'La Liga',
    '135': 'Serie A',
    '78': 'Bundesliga',
    '61': 'Ligue 1',
    '88': 'Eredivisie',
    '94': 'Primeira Liga',
    '203': 'Süper Lig',
    '307': 'Saudi Pro League',
    '253': 'MLS',
    '40': 'Championship',
    '292': 'K League 1',
    '98': 'J1 League',
    '71': 'Brasileiro Série A',
    '262': 'Liga MX',
    '128': 'Liga Profesional',
    '144': 'Jupiler Pro League',
    '179': 'Premiership',
    '1': 'World Cup',
    '2': 'Champions League',
    '3': 'Europa League'
};

const CustomTooltip = ({ active, payload, label, lang }: any) => {
    if (active && payload && payload.length) {
        const name = payload[0].name;
        const displayName = LEAGUE_NAME_MAP[name] || name;
        return (
            <div className="bg-white border border-slate-200 p-3 rounded-xl shadow-xl z-50">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 border-b border-slate-100 pb-2">{displayName}</p>
                {payload.map((entry: any, index: number) => (
                    <div key={index} className="flex items-center justify-between gap-6 mb-1 last:mb-0">
                        <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: entry.color || entry.stroke || entry.fill }} />
                            <span className="text-[10px] font-bold text-slate-600 uppercase tracking-tight">{at(lang, 'activeFans')}</span>
                        </div>
                        <span className="text-xs font-black tabular-nums text-slate-900">{entry.value?.toLocaleString()}</span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const GrowthTooltip = ({ active, payload, label, lang }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white border border-slate-200 p-3 rounded-xl shadow-xl z-50">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 border-b border-slate-100 pb-2">{label}</p>
                {payload.map((entry: any, index: number) => (
                    <div key={index} className="flex items-center justify-between gap-6 mb-1 last:mb-0">
                        <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: entry.color || entry.stroke || entry.fill }} />
                            <span className="text-[10px] font-bold text-slate-600 uppercase tracking-tight">
                                {entry.name === 'highlights' ? at(lang, 'highlights') : 
                                 entry.name === 'shorts' ? at(lang, 'shorts') : 
                                 entry.name === 'activity' ? at(lang, 'feedActivity') : 
                                 entry.name === 'registrations' ? at(lang, 'newAgents') : entry.name}
                            </span>
                        </div>
                        <span className="text-xs font-black tabular-nums text-slate-900">{entry.value?.toLocaleString()}</span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

type TimeRange = '7d' | '30d' | '90d';

export const OverviewTab: React.FC<OverviewTabProps> = ({ stats, allUsers, onTabSwitch }) => {
    const { lang } = useLanguage();
    const [chartData, setChartData] = useState<{leagueData: any[], dailyStats: any[]} | null>(null);
    const [loadingCharts, setLoadingCharts] = useState(true);
    const [selectedRange, setSelectedRange] = useState<TimeRange>('30d');

    const loadChartData = async (range: TimeRange) => {
        setLoadingCharts(true);
        try {
            const data = await fetchAdminChartsData(range);
            if (data) {
                setChartData(data);
                setLoadingCharts(false);
            } else {
                setLoadingCharts(false);
            }
        } catch (e) {
            console.error("Chart data loading failed:", e);
            setLoadingCharts(false);
        }
    };

    const [shouldRenderCharts, setShouldRenderCharts] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShouldRenderCharts(true);
        }, 2000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (shouldRenderCharts) {
            loadChartData(selectedRange);
        }
    }, [selectedRange, shouldRenderCharts]);

    const statCards = [
        { 
            label: at(lang, 'totalAgents'), 
            value: stats.users?.toLocaleString() || '0', 
            change: `+${stats.usersGrowth || 0} ${at(lang, 'lastMonthContrast')}`,
            icon: Users, 
            color: 'text-slate-900',
            isPositive: true
        },
        { 
            label: at(lang, 'intelFeed'), 
            value: stats.posts?.toLocaleString() || '0', 
            change: `${parseFloat(stats.activityChange) > 0 ? '+' : ''}${stats.activityChange}% ${at(lang, 'yesterdayContrast')}`,
            icon: Activity, 
            color: 'text-slate-900',
            isPositive: parseFloat(stats.activityChange) >= 0
        },
        { 
            label: at(lang, 'securityAlerts'), 
            value: stats.reports || 0, 
            change: at(lang, 'safetyClearance'), 
            icon: ShieldAlert, 
            color: 'text-rose-500',
            onClick: () => onTabSwitch('reports')
        },
        { 
            label: at(lang, 'communityActivity'), 
            value: stats.comments?.toLocaleString() || '0', 
            change: at(lang, 'totalCommentsReplies'), 
            icon: MessageSquare, 
            color: 'text-indigo-500',
            onClick: () => onTabSwitch('content')
        }
    ];

    const rangeOptions: { id: TimeRange; label: string }[] = [
        { id: '7d', label: at(lang, 'last7days') },
        { id: '30d', label: at(lang, 'last30days') },
        { id: '90d', label: at(lang, 'last3months') }
    ];

    const totalSectorCounts = useMemo(() => {
        return chartData?.leagueData.reduce((acc, curr) => acc + curr.count, 0) || 1;
    }, [chartData]);

    return (
        <div className="space-y-10 animate-in fade-in duration-700">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">{at(lang, 'operationsHub')}</h1>
                    <p className="text-sm font-medium text-slate-500 mt-1">{at(lang, 'opsHubDesc')}</p>
                </div>
                <div className="flex items-center gap-3">
                    <button className="px-5 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-lg shadow-slate-100 active:scale-95 transition-all">{at(lang, 'exportIntel')}</button>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {statCards.map((card, i) => (
                    <div 
                        key={i} 
                        onClick={card.onClick}
                        className={`bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between h-40 group md:hover:border-slate-900 transition-colors ${card.onClick ? 'cursor-pointer' : ''}`}
                    >
                        <div className="flex justify-between items-start">
                            <span className="text-[11px] font-black text-slate-500 uppercase tracking-widest">{card.label}</span>
                            <card.icon size={16} className={`${card.color} opacity-60 md:group-hover:opacity-100 transition-opacity`} />
                        </div>
                        <div className="mt-4">
                            <span className={`text-2xl font-black text-slate-900 tabular-nums`}>{card.value}</span>
                            <p className={`text-[10px] font-bold mt-1.5 flex items-center gap-1 ${card.isPositive ? 'text-emerald-500' : card.isPositive === false ? 'text-rose-500' : 'text-slate-400'}`}>
                                {card.isPositive ? <ArrowUpRight size={10} /> : card.isPositive === false ? <ArrowDownRight size={10} /> : null}
                                {card.change}
                            </p>
                        </div>
                    </div>
                ))}
            </div>

            <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm p-8 flex flex-col">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-10 gap-6">
                    <div>
                        <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">{at(lang, 'growthAnalytics')}</h3>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{at(lang, 'growthDesc')}</p>
                    </div>
                    <div className="flex items-center bg-slate-100 p-1 rounded-2xl border border-slate-200 self-start sm:self-center">
                        {rangeOptions.map(option => (
                            <button
                                key={option.id}
                                onClick={() => setSelectedRange(option.id)}
                                className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase transition-all ${
                                    selectedRange === option.id 
                                    ? 'bg-white text-slate-900 shadow-sm' 
                                    : 'text-slate-400 md:hover:text-slate-600'
                                }`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
                
                <div className="w-full aspect-[4/3] md:aspect-[21/9] min-h-[350px] relative bg-slate-50/30 rounded-3xl overflow-hidden border border-slate-100">
                    {(!shouldRenderCharts || loadingCharts) && (
                        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-4 bg-white">
                            <Loader2 className="animate-spin text-rose-500" size={32} />
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest animate-pulse">{at(lang, 'syncingIntel')}</p>
                        </div>
                    )}
                    {shouldRenderCharts && chartData?.dailyStats && chartData.dailyStats.length > 0 ? (
                        <ResponsiveContainer width="99%" height="99%" debounce={50}>
                            <AreaChart data={chartData.dailyStats} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorRecruits" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.15}/>
                                        <stop offset="95%" stopColor="#f43f5e" stopOpacity={0}/>
                                    </linearGradient>
                                    <linearGradient id="colorAct" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#0f172a" stopOpacity={0.15}/>
                                        <stop offset="95%" stopColor="#0f172a" stopOpacity={0}/>
                                    </linearGradient>
                                    <linearGradient id="colorHighlights" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15}/>
                                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                                    </linearGradient>
                                    <linearGradient id="colorShorts" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.15}/>
                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis 
                                    dataKey="date" 
                                    axisLine={false} 
                                    tickLine={false} 
                                    tick={{fontSize: 9, fill: '#94a3b8', fontWeight: 800}} 
                                    dy={10} 
                                    interval={selectedRange === '90d' ? 6 : selectedRange === '30d' ? 2 : 0}
                                />
                                <YAxis axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#94a3b8', fontWeight: 800}} />
                                <Tooltip content={<GrowthTooltip lang={lang} />} />
                                <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px', fontSize: '10px', fontWeight: '900', textTransform: 'uppercase' }} />
                                <Area 
                                    name={at(lang, 'feedActivity')}
                                    type="monotone" 
                                    dataKey="activity" 
                                    stroke="#0f172a" 
                                    strokeWidth={3}
                                    fillOpacity={1} 
                                    fill="url(#colorAct)" 
                                    animationDuration={1500}
                                />
                                <Area 
                                    name={at(lang, 'highlights')} 
                                    type="monotone" 
                                    dataKey="highlights" 
                                    stroke="#6366f1" 
                                    strokeWidth={3}
                                    fillOpacity={1} 
                                    fill="url(#colorHighlights)" 
                                    animationDuration={1500}
                                />
                                <Area 
                                    name={at(lang, 'shorts')} 
                                    type="monotone" 
                                    dataKey="shorts" 
                                    stroke="#10b981" 
                                    strokeWidth={3}
                                    fillOpacity={1} 
                                    fill="url(#colorShorts)" 
                                    animationDuration={1500}
                                />
                                <Area 
                                    name={at(lang, 'newAgents')} 
                                    type="monotone" 
                                    dataKey="registrations" 
                                    stroke="#f43f5e" 
                                    strokeWidth={3}
                                    fillOpacity={1} 
                                    fill="url(#colorRecruits)" 
                                    animationDuration={1500}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center opacity-20">
                            <Activity size={48} />
                            <p className="text-[10px] font-black uppercase mt-4 tracking-widest">{at(lang, 'noStreamData')}</p>
                        </div>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 gap-8">
                <div className="bg-white rounded-[40px] border border-slate-200 shadow-sm p-8 md:p-10 flex flex-col min-h-[500px]">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">{at(lang, 'activeSectors')}</h3>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{at(lang, 'sectorDesc')}</p>
                        </div>
                        <div className="w-12 h-12 rounded-[20px] bg-slate-50 flex items-center justify-center text-slate-400">
                            <Globe size={20} />
                        </div>
                    </div>
                    
                    <div className="flex-1 flex flex-col gap-6 relative min-h-[300px]">
                        {(!shouldRenderCharts || loadingCharts) && (
                            <div className="absolute inset-0 z-30 flex items-center justify-center bg-white">
                                <Loader2 className="animate-spin text-rose-500" size={32} />
                            </div>
                        )}
                        {shouldRenderCharts && chartData?.leagueData && chartData.leagueData.length > 0 ? (
                            <>
                                <div className="w-full aspect-[16/9] md:aspect-square max-w-[240px] mx-auto relative flex items-center justify-center">
                                    <ResponsiveContainer width="99%" height="99%" debounce={50}>
                                        <PieChart>
                                            <Pie
                                                data={chartData.leagueData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius="70%"
                                                outerRadius="100%"
                                                paddingAngle={5}
                                                dataKey="count"
                                                nameKey="name"
                                                stroke="none"
                                                animationDuration={1500}
                                            >
                                                {chartData.leagueData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip content={<CustomTooltip lang={lang} />} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="absolute flex flex-col items-center justify-center text-center">
                                        <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em] mb-1">{at(lang, 'total')}</span>
                                        <span className="text-3xl font-black text-slate-900 tabular-nums">{totalSectorCounts}</span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-3 max-h-[220px] overflow-y-auto custom-scrollbar pr-2">
                                    {chartData.leagueData.slice(0, 10).map((entry, idx) => {
                                        const percentage = ((entry.count / totalSectorCounts) * 100).toFixed(1);
                                        const leagueName = LEAGUE_NAME_MAP[entry.name] || entry.name;
                                        return (
                                            <div key={idx} className="group p-3 bg-slate-50/50 border border-slate-100/50 rounded-2xl transition-all cursor-default">
                                                <div className="flex items-center justify-between mb-1.5">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <div className="w-2 h-2 rounded-full shrink-0 shadow-sm" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                                                        <span className="text-[10px] font-black text-slate-800 uppercase truncate tracking-tight">{leagueName}</span>
                                                    </div>
                                                    <span className="text-[10px] font-black text-slate-900 tabular-nums">{entry.count.toLocaleString()} <span className="text-slate-400 font-bold ml-1">({percentage}%)</span></span>
                                                </div>
                                                <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
                                                    <div 
                                                        className="h-full rounded-full transition-all duration-1000 ease-out" 
                                                        style={{ 
                                                            width: `${percentage}%`,
                                                            backgroundColor: COLORS[idx % COLORS.length]
                                                        }} 
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center opacity-10">
                                <Globe size={64} strokeWidth={1} />
                                <p className="text-sm font-black uppercase mt-6 tracking-widest">{at(lang, 'scanningGrid')}</p>
                            </div>
                        )}
                    </div>
                </div>

            </div>

            <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-8 md:p-10 border-b border-slate-100 flex items-center justify-between">
                    <div>
                        <h3 className="font-black text-slate-900 uppercase tracking-tight text-sm">{at(lang, 'recentPersonnel')}</h3>
                        <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-widest">{at(lang, 'personnelDesc')}</p>
                    </div>
                    <button onClick={() => onTabSwitch('users')} className="px-5 py-2.5 bg-slate-50 text-slate-900 rounded-xl text-[10px] font-black uppercase tracking-widest md:hover:bg-slate-900 md:hover:text-white transition-all shadow-sm active:scale-95 flex items-center gap-2">
                        {at(lang, 'viewAll')} <ArrowRight size={12} strokeWidth={3} />
                    </button>
                </div>
                <div className="overflow-x-auto custom-scrollbar">
                    <div className="flex p-8 gap-6 min-w-max">
                        {allUsers.slice(0, 10).map((u, i) => (
                            <div key={u.id} className="w-48 p-6 bg-slate-50/50 rounded-[32px] flex flex-col items-center text-center group cursor-pointer md:hover:bg-white md:hover:shadow-xl transition-all border border-transparent md:hover:border-slate-100 animate-in fade-in zoom-in-95" style={{ animationDelay: `${i * 50}ms` }} onClick={() => onTabSwitch('users')}>
                                <div className="relative mb-5">
                                    <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-white shadow-md ring-1 ring-slate-100 md:group-hover:scale-105 transition-transform duration-500">
                                        <img src={u.avatar || `https://ui-avatars.com/api/?name=${u.name}&background=random`} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                                        <UserPlus size={10} strokeWidth={4} />
                                    </div>
                                </div>
                                <span className="text-sm font-black text-slate-900 uppercase tracking-tight truncate w-full md:group-hover:text-rose-500 transition-colors">{u.name}</span>
                                <span className="text-[9px] text-slate-400 font-bold uppercase mt-1 tabular-nums tracking-widest">{new Date(u.joinDate).toLocaleDateString()}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
